import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  #selectionGeneration = 0;
  #revealController;

  connectedCallback() {
    super.connectedCallback();

    if (this.#controller.signal.aborted) this.#controller = new AbortController();
    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#selectionGeneration++;
    this.#revealController?.abort();
    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    this.#revealController?.abort();
    const generation = ++this.#selectionGeneration;
    const picker = event.target.closest('variant-picker');
    const userInitiated = event.detail?.userInitiated === true &&
      picker?.dataset.templateProductMatch === 'true' &&
      !picker.closest('dialog, product-card, quick-add-dialog, featured-product-information') &&
      !this.closest('dialog, product-card, quick-add-dialog, featured-product-information');
    let previousVariant = null;

    if (userInitiated) {
      try {
        previousVariant = JSON.parse(picker.querySelector('script[type="application/json"]')?.textContent || 'null');
      } catch {}
    }

    event.promise
      .then(({ detail }) => {
        if (generation !== this.#selectionGeneration || !this.isConnected || !detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        const variantId = detail.resource?.id;
        const revealMedia = userInitiated && variantId &&
          String(previousVariant?.id ?? '') !== String(variantId);

        this.replaceWith(newMediaGallery);
        if (revealMedia && newMediaGallery instanceof MediaGallery) {
          newMediaGallery.#selectFirstMedia();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  #selectFirstMedia() {
    this.#revealController?.abort();
    const controller = new AbortController();
    this.#revealController = controller;
    const generation = this.#selectionGeneration;
    let frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => {
        if (controller.signal.aborted || !this.isConnected || generation !== this.#selectionGeneration) return;

        const slideshow = this.slideshow;
        if (slideshow?.current > 0) slideshow.select(0, undefined, { animate: false });
      });
    });

    controller.signal.addEventListener('abort', () => {
      cancelAnimationFrame(frame);
    }, { once: true });
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}


